import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/supabaseClient";

export interface NotificationPreferences {
  id: string;
  user_id: string;
  notify_due_soon: boolean;
  notify_overdue: boolean;
  notify_auto_renewal: boolean;
  notify_trial_ending: boolean;
  days_before_due: number;
  days_before_renewal: number;
  days_before_trial_end: number;
  created_at: string;
  updated_at: string;
}

export const useNotificationPreferences = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["notificationPreferences", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // Create default preferences if none exist
      if (!data) {
        const { data: newPrefs, error: insertError } = await supabase
          .from("user_notification_preferences")
          .insert({ user_id: user.id })
          .select()
          .single();

        if (insertError) throw insertError;
        return newPrefs as NotificationPreferences;
      }

      return data as NotificationPreferences;
    },
    enabled: !!user,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("user_notification_preferences")
        .update(updates)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notificationPreferences", user?.id],
      });
      toast.success("Preferences updated", { description: "Your notification preferences have been saved." });
    },
    onError: (error) => {
      console.error("Failed to update preferences:", error);
      toast.error("Update failed", { description: "Could not save your preferences. Please try again." });
    },
  });

  return {
    preferences,
    isLoading,
    error,
    updatePreferences,
  };
};
